import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Button, Input, Radio, Space, Tag, Tooltip } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { IAppUserAuto, ICountAppUserAutoStats } from "../../interfaces";

import { EmitError } from "../../utils/EmitError";
import { formatCpfCnpj, onlyNumbers } from "../../utils/Masks";
import { formatDateStringToDate, formatDateStringToDateAndTime, poeValorComPontosEmMil } from "../../utils/Conversores";
import { removeAcento } from "../../utils/RemoveAcentos";
import useAuth from "../../hooks/useAuth";
import AppUsersAutoService from "../../services/http/services/AppUsersAutoService";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableData extends IAppUserAuto {
    key: number | undefined;
    documento: 'CPF' | 'CNPJ';
}

export interface IAppUserAutoFilters {
    name: string | null;
    cpfCnpj: string | null;
    documento: 'CPF' | 'CNPJ' | null;
    created_at: string | null;
}

export type SortField = 'name' | 'totalTicket' | 'created_at' | 'cpfCnpj' | null;

export default function useAppUsersAuto() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [totalAppUsers, setTotalAppUsers] = useState<ICountAppUserAutoStats>();
    const [pagination, setPagination] = useState({ current: 1, pageSize: 50 });

    const [filters, setFilters] = useState<IAppUserAutoFilters>({
        name: null,
        cpfCnpj: null,
        documento: null,
        created_at: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'totalTicket',
        order: 'desc',
    });

    const [searchName, setSearchName] = useState('');
    const [searchCpfCnpj, setSearchCpfCnpj] = useState('');
    const [searchDate, setSearchDate] = useState('');

    const nameInputRef = useRef<any>(null);
    const cpfCnpjInputRef = useRef<any>(null);
    const dateInputRef = useRef<any>(null);

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const handleFilterChange = useCallback((key: keyof IAppUserAutoFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            name: null,
            cpfCnpj: null,
            documento: null,
            created_at: null,
        });
        setSearchName('');
        setSearchCpfCnpj('');
        setSearchDate('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.name) {
            const query = removeAcento(filters.name.trim().toLowerCase());
            result = result.filter((item) =>
                item.name ? removeAcento(item.name.toLowerCase()).includes(query) : false
            );
        }

        if (filters.cpfCnpj) {
            const query = onlyNumbers(filters.cpfCnpj);
            result = result.filter((item) => {
                const raw = onlyNumbers(item.cpfCnpj || '');
                return raw.includes(query);
            });
        }

        if (filters.documento) {
            result = result.filter((item) => item.documento === filters.documento);
        }

        if (filters.created_at) {
            const query = filters.created_at.trim();
            result = result.filter((item) => {
                if (!item.created_at) return false;
                const formatted = formatDateStringToDate(item.created_at);
                return formatted.includes(query);
            });
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'name') {
                    const comp = (a.name || '').localeCompare(b.name || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'totalTicket') {
                    const valA = a.totalTicket || 0;
                    const valB = b.totalTicket || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'created_at') {
                    const valA = new Date(a.created_at || 0).getTime();
                    const valB = new Date(b.created_at || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        filters.name
            ? {
                  key: 'name',
                  label: 'Nome',
                  value: filters.name,
                  onClear: () => {
                      setSearchName('');
                      handleFilterChange('name', null);
                  },
              }
            : null,
        filters.documento
            ? {
                  key: 'documento',
                  label: 'Tipo',
                  value: filters.documento === 'CPF' ? 'Pessoa Física (CPF)' : 'Pessoa Jurídica (CNPJ)',
                  onClear: () => handleFilterChange('documento', null),
              }
            : null,
        filters.cpfCnpj
            ? {
                  key: 'cpfCnpj',
                  label: 'CPF/CNPJ',
                  value: formatCpfCnpj(filters.cpfCnpj),
                  onClear: () => {
                      setSearchCpfCnpj('');
                      handleFilterChange('cpfCnpj', null);
                  },
              }
            : null,
        filters.created_at
            ? {
                  key: 'created_at',
                  label: 'Data de Cadastro',
                  value: filters.created_at,
                  onClear: () => {
                      setSearchDate('');
                      handleFilterChange('created_at', null);
                  },
              }
            : null,
    ];

    const getSortOrder = (field: SortField) => {
        if (sorter.field === field) {
            return sorter.order === 'asc' ? ('ascend' as const) : ('descend' as const);
        }
        return null;
    };

    const handleTableChange = (newPagination: any, tableFilters: any, newSorter: any) => {
        if (newPagination) {
            setPagination({
                current: newPagination.current || 1,
                pageSize: newPagination.pageSize || 50,
            });
        }

        if (newSorter && newSorter.field) {
            setSorter({
                field: newSorter.field as SortField,
                order: newSorter.order === 'ascend' ? 'asc' : newSorter.order === 'descend' ? 'desc' : null,
            });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            sortOrder: getSortOrder('name'),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Buscar por Nome
                    </div>
                    <Input
                        ref={nameInputRef}
                        placeholder="Digite o nome..."
                        value={searchName}
                        onChange={(e) => setSearchName(e.target.value)}
                        onPressEnter={() => {
                            handleFilterChange('name', searchName.trim() || null);
                            confirm();
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                handleFilterChange('name', searchName.trim() || null);
                                confirm();
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchName('');
                                handleFilterChange('name', null);
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: filters.name ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => nameInputRef.current?.focus(), 100);
                }
            },
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'nowrap' }}>
                    <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                        {filters.name ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[filters.name]}
                                autoEscape
                                textToHighlight={record.name || '--'}
                            />
                        ) : (
                            record.name || '--'
                        )}
                    </span>
                </div>
            ),
            width: '32%',
        },
        {
            title: 'Tipo',
            dataIndex: 'documento',
            key: 'documento',
            align: 'center' as const,
            width: '10%',
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 210, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Tipo de Documento
                    </div>
                    <Radio.Group
                        value={filters.documento || 'ALL'}
                        onChange={(e) => {
                            const val = e.target.value === 'ALL' ? null : e.target.value;
                            handleFilterChange('documento', val);
                            confirm();
                        }}
                        style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                    >
                        <Radio value="ALL">Todos</Radio>
                        <Radio value="CPF">Pessoa Física (CPF)</Radio>
                        <Radio value="CNPJ">Pessoa Jurídica (CNPJ)</Radio>
                    </Radio.Group>
                    <Button
                        size="small"
                        onClick={() => {
                            handleFilterChange('documento', null);
                            if (clearFilters) clearFilters();
                            confirm();
                        }}
                        style={{ marginTop: 4 }}
                    >
                        Limpar
                    </Button>
                </div>
            ),
            filterIcon: () => (
                <FilterOutlined style={{ color: filters.documento ? '#1677ff' : undefined }} />
            ),
            render: (doc: 'CPF' | 'CNPJ') => (
                <Tag
                    color={doc === 'CNPJ' ? 'purple' : 'blue'}
                    style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
                >
                    {doc}
                </Tag>
            ),
        },
        {
            title: 'CPF / CNPJ',
            dataIndex: 'cpfCnpj',
            key: 'cpfCnpj',
            width: '18%',
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 260, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Buscar CPF ou CNPJ
                    </div>
                    <Input
                        ref={cpfCnpjInputRef}
                        placeholder="000.000.000-00 ou CNPJ"
                        value={searchCpfCnpj}
                        onChange={(e) => setSearchCpfCnpj(formatCpfCnpj(e.target.value))}
                        onPressEnter={() => {
                            handleFilterChange('cpfCnpj', searchCpfCnpj.trim() || null);
                            confirm();
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                handleFilterChange('cpfCnpj', searchCpfCnpj.trim() || null);
                                confirm();
                            }}
                            style={{ minWidth: 100 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchCpfCnpj('');
                                handleFilterChange('cpfCnpj', null);
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            style={{ minWidth: 80 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: filters.cpfCnpj ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => cpfCnpjInputRef.current?.focus(), 100);
                }
            },
            render: (cpfCnpj: string) => (
                <span style={{ color: isDark ? '#a6a6a6' : '#64748b', fontSize: 13, letterSpacing: '0.3px', whiteSpace: 'nowrap' }}>
                    {filters.cpfCnpj ? (
                        <Highlighter
                            highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                            searchWords={[filters.cpfCnpj, onlyNumbers(filters.cpfCnpj)]}
                            autoEscape
                            textToHighlight={cpfCnpj ? formatCpfCnpj(cpfCnpj) : '--'}
                        />
                    ) : (
                        cpfCnpj ? formatCpfCnpj(cpfCnpj) : '--'
                    )}
                </span>
            ),
        },
        {
            title: 'Bilhetes',
            dataIndex: 'totalTicket',
            key: 'totalTicket',
            align: 'center' as const,
            width: '10%',
            sorter: true,
            sortOrder: getSortOrder('totalTicket'),
            showSorterTooltip: false,
            render: (totalTicket?: number) => (
                <Tooltip color="#1677ff" title="Total de bilhetes gerados">
                    <span style={{ fontWeight: 600, color: '#1677ff', fontSize: 14, whiteSpace: 'nowrap' }}>
                        {poeValorComPontosEmMil(totalTicket) ?? '0'}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            align: 'center' as const,
            width: '16%',
            sorter: true,
            sortOrder: getSortOrder('created_at'),
            showSorterTooltip: false,
            filterDropdown: ({ confirm, clearFilters }: any) => (
                <div style={{ padding: 12, width: 240, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: isDark ? '#ccc' : '#475569', marginBottom: 2 }}>
                        Filtrar por Data (DD/MM/AAAA)
                    </div>
                    <Input
                        ref={dateInputRef}
                        placeholder="Ex: 10/09/2026"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        onPressEnter={() => {
                            handleFilterChange('created_at', searchDate.trim() || null);
                            confirm();
                        }}
                        allowClear
                    />
                    <Space style={{ justifyContent: 'space-between', marginTop: 4 }}>
                        <Button
                            type="primary"
                            icon={<SearchOutlined />}
                            size="small"
                            onClick={() => {
                                handleFilterChange('created_at', searchDate.trim() || null);
                                confirm();
                            }}
                            style={{ minWidth: 90 }}
                        >
                            Pesquisar
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                setSearchDate('');
                                handleFilterChange('created_at', null);
                                if (clearFilters) clearFilters();
                                confirm();
                            }}
                            style={{ minWidth: 70 }}
                        >
                            Limpar
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: () => (
                <SearchOutlined style={{ color: filters.created_at ? '#1677ff' : undefined }} />
            ),
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => dateInputRef.current?.focus(), 100);
                }
            },
            render: (criado: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {criado ? formatDateStringToDateAndTime(criado, true) : '--'}
                </span>
            ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: '8%',
            render: (_: any, record: ITableData) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <TableActionButton
                        action="view"
                        tooltip="Visualizar usuário"
                        onClick={(e) => {
                            e?.stopPropagation?.();
                            navigate(`/usuarios/auto/${record.id}`);
                        }}
                    />
                </div>
            ),
        },
    ];

    const setTableDataFromRequest = useCallback((data: Array<IAppUserAuto>) => {
        const newData: Array<ITableData> = data.map((user) => ({
            ...user,
            key: user.id,
            documento: user.cpfCnpj && user.cpfCnpj.length > 11 ? 'CNPJ' : 'CPF',
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        if (!activeCity) {
            return;
        }

        const controller = new AbortController();

        async function loadAppUsers() {
            try {
                setIsLoading(true);

                const [appUsersList, countTotalAppUsers] = await Promise.all([
                    AppUsersAutoService.listAppUsersAuto(activeCity?.id!, controller.signal),
                    AppUsersAutoService.getCountAppUserAutoByCityId(activeCity?.id!, controller.signal)
                ]);

                setTableDataFromRequest(appUsersList);
                setTotalAppUsers(countTotalAppUsers);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'appUsersAuto_loadError');
                setIsLoading(false);
            }
        }

        loadAppUsers();

        return () => {
            controller.abort();
        }
    }, [activeCity, setTableDataFromRequest]);

    return {
        isLoading,
        columns,
        tableData: filteredData,
        totalAppUsers,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        navigate,
    };
}
